import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { selectJSONWebhooksConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { LinkOutlined } from '@ant-design/icons';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../common/components/TableGrid/TableGrid';
import { autoIncrementedName } from '../../../../../common/helpers/name-autoincrement';
import {
  ComponentDataItem,
  DefaultCell,
} from '../../../contacts/components/ContactsTable/ContactTable';
import './ConnectionsPanel.pcss';
import { useViewActions } from '@mm/state/actions/viewActions';
import { ReactComponent as AddWebhookIcon } from '../../../beta/modules/Integrations/icons/add-webhook.svg';
import { WebhookIcon } from '@mm/icons';
import { BaseIntegration } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegrationHeader';

type Props = {
  cellData: ComponentDataItem[];
};

export const UNTITLED_CONNECTION_NAME = 'Untitled connection';

export const ConnectionsPanel = (props: Props) => {
  const { cellData } = props;
  const connections = useSelector(selectJSONWebhooksConnections);
  const connectedPageId = useSelector(activeFacebookPageIdSelector);
  const connectionsCRUDActions = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();

  const gridTemplateColumns = `20% 15% 20% 20% 15% 10%`;

  const onNewConnection = async () => {
    const name = autoIncrementedName(
      UNTITLED_CONNECTION_NAME,
      connections.map(c => c.name),
    );

    const connection = await connectionsCRUDActions.create(connectedPageId, {
      name,
      connectionType: 'Webhook',
      description: '',
    });

    if (connection) {
      showSlideoutMenu('editJSONWebhookIntegrationEditor', {
        webhookId: connection.id,
      });
    }
  };

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_JSON_WEBHOOKS,
    featureFlag: 'jsonWebhook',
  };

  const header = (
    <div className="integrations__header-actions">
      {!!connections.length && (
        <div className="integrations__header-status">
          Connections
          <LinkOutlined className="integrations__header-status-icon" />
          <div className="integrations__header-status-count">
            {connections?.length || 0}
          </div>
        </div>
      )}
      <PrimaryButton
        className="integrations__header-add-button-secondary"
        onClick={e => {
          e?.stopPropagation();
          onNewConnection();
        }}
        color={ButtonColor.secondaryBlue}
      >
        Add a Connection
      </PrimaryButton>
    </div>
  );

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="JSON Webhooks "
          subtitle="Automatically send data from Customers.ai to JSON Webhook"
          icon={<WebhookIcon color="white" />}
          headerRightComponent={header}
          upgradeButtonName="Add a Connection"
          css={{ icon: 'integrations__webhook-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="default"
      emptyStateConfig={{
        button: 'Add a JSON Webhooks Connection',
        onClick: onNewConnection,
        icon: <AddWebhookIcon />,
        ...sectionProps,
      }}
      showEmptyState={!connections.length}
    >
      <Grid className="integrations-grid">
        {!!connections.length && (
          <>
            <GridLabels
              className="integrations-grid__labels connections-panel__grid-labels"
              gridTemplateColumns={gridTemplateColumns}
            >
              {cellData.map(cell => (
                <TableLabel
                  key={cell.keyName || cell.label}
                  dataItem={cell}
                  orderByAtTheEnd
                />
              ))}
            </GridLabels>

            <div className="integrations-grid__rows-container">
              {connections.map(item => (
                <GridRow
                  key={item.id}
                  gridTemplateColumns={gridTemplateColumns}
                >
                  {cellData.map(({ Cell, keyName }) =>
                    Cell ? (
                      <Cell key={keyName} item={item} />
                    ) : (
                      <DefaultCell
                        key={keyName}
                        keyName={keyName as string}
                        item={item}
                      />
                    ),
                  )}
                </GridRow>
              ))}
            </div>
          </>
        )}
      </Grid>
    </BaseIntegration>
  );
};
